/**
 * NotificationItem — renders a single notification entry with actor name,
 * event description, relative timestamp, and read/unread styling.
 *
 * Requirements: 4.4, 4.5
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import type { NotificationItem as NotificationItemType } from '../api/notifications';

// ---------------------------------------------------------------------------
// Relative timestamp formatting
// ---------------------------------------------------------------------------

function formatRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;

  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface NotificationItemProps {
  readonly notification: NotificationItemType;
  readonly onMarkRead: (notificationId: string) => Promise<void>;
  readonly onClose?: () => void;
}

export function NotificationItem({
  notification,
  onMarkRead,
  onClose,
}: NotificationItemProps): React.ReactElement {
  const navigate = useNavigate();

  function handleClick(): void {
    // Mark as read
    if (!notification.read) {
      onMarkRead(notification.notificationId);
    }

    // Navigate to relevant context
    if (notification.pageId && notification.libraryId) {
      // Page-level notification — deep link to the specific page
      navigate(`/pages/${notification.libraryId}/${notification.pageId}`);
    } else if (notification.pageId) {
      // Page notification without libraryId — navigate to Pages module
      navigate('/pages');
    } else if (notification.boardId && notification.cardId) {
      // Card-level notification (comment mention, assignment, etc.)
      navigate(`/boards/${notification.boardId}/cards/${notification.cardId}`);
    } else if (notification.boardId) {
      // Board-level notification (invite, visibility change, etc.)
      navigate(`/boards/${notification.boardId}`);
    }

    // Close the feed popover
    onClose?.();
  }

  function handleKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  }

  return (
    <li
      className={cn(
        'px-3 py-2 cursor-pointer transition-colors rounded-sm',
        'hover:bg-accent focus-within:bg-accent',
        !notification.read && 'border-l-2 border-l-primary bg-primary/5',
        notification.read && 'border-l-2 border-l-transparent',
      )}
      data-read={notification.read}
    >
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className="w-full text-left focus:outline-none"
        aria-label={`${notification.read ? '' : 'Unread: '}${notification.actorName} ${notification.message}`}
      >
        <span className={cn('text-sm', !notification.read && 'font-semibold')}>
          {notification.actorName}
        </span>{' '}
        <span className="text-sm text-muted-foreground">{notification.message}</span>
        <time
          dateTime={notification.createdAt}
          className="block text-xs text-muted-foreground mt-0.5"
        >
          {formatRelativeTime(notification.createdAt)}
        </time>
      </button>
    </li>
  );
}

// Export utility for testing
export { formatRelativeTime };
